import { useEffect, useState } from "react";
import { Pencil, Plus, Search, Star, Trash2, UserRound } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import ConfirmDialog from "@/components/ConfirmDialog";
import { api, fmtBR, fmtBRL, fmtMonthBR, formatApiError, WEEKDAY_NAMES } from "@/lib/api";
import { EnrollmentStatusBadge } from "@/pages/Enrollments";
import { ChargeStatusBadge } from "@/pages/Financial";
import { PedagogyPanel } from "@/components/PedagogyPanel";
import { PedagogicalReportDialog } from "@/components/PedagogicalReport";

const STATUS_LABELS = { ativo: "Ativo", inativo: "Inativo", trancado: "Trancado", cancelado: "Cancelado" };
const RELATIONSHIP_LABELS = { responsavel: "Responsável", pai: "Pai", mae: "Mãe", avo: "Avô/Avó", tutor: "Tutor", outro: "Outro" };

export const fmtCPF = (cpf) => (cpf ? cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4") : "—");

export function calcAge(birthDate) {
  const b = new Date(`${birthDate}T12:00:00`);
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age -= 1;
  return age;
}

const EMPTY = {
  full_name: "", birth_date: "", cpf: "", whatsapp: "", email: "",
  address: "", photo_url: "", admin_notes: "", status: "ativo",
};

export const StatusBadge = ({ status }) => (
  <Badge
    variant={status === "ativo" ? "default" : status === "cancelado" ? "destructive" : "secondary"}
    className={status === "ativo" ? "bg-primary text-primary-foreground" : ""}
  >
    {STATUS_LABELS[status] || status}
  </Badge>
);

export default function Students() {
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailEnrollments, setDetailEnrollments] = useState([]);
  const [detailFin, setDetailFin] = useState(null);
  const [detailAtt, setDetailAtt] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [allGuardians, setAllGuardians] = useState([]);
  const [linkForm, setLinkForm] = useState({ guardian_id: "", relationship: "responsavel", is_primary: false });

  const load = async () => {
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (statusFilter !== "todos") params.status = statusFilter;
    const { data } = await api.get("/students", { params });
    setStudents(data);
  };

  useEffect(() => {
    load().catch((e) => toast.error(formatApiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const openDialog = (student) => {
    setEditing(student || null);
    setForm(
      student
        ? {
            full_name: student.full_name, birth_date: student.birth_date, cpf: student.cpf || "",
            whatsapp: student.whatsapp || "", email: student.email || "", address: student.address || "",
            photo_url: student.photo_url || "", admin_notes: student.admin_notes || "", status: student.status,
          }
        : EMPTY
    );
    setError("");
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = { ...form };
      ["cpf", "whatsapp", "email", "address", "photo_url", "admin_notes"].forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
      });
      if (editing) await api.put(`/students/${editing.id}`, payload);
      else await api.post("/students", payload);
      toast.success(editing ? "Aluno atualizado." : "Aluno cadastrado.");
      setDialogOpen(false);
      load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/students/${deleting.id}`);
      toast.success("Aluno excluído.");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
      setDeleting(null);
    }
  };

  const openDetail = async (student) => {
    try {
      const [{ data: detailData }, { data: guardiansData }, { data: enrollmentsData }, { data: finData }, { data: attData }] = await Promise.all([
        api.get(`/students/${student.id}`),
        api.get("/guardians"),
        api.get("/enrollments", { params: { student_id: student.id } }),
        api.get(`/financial/students/${student.id}`).catch((e) => {
          if (e?.response?.status === 404) return { data: null };
          throw e;
        }),
        api.get(`/students/${student.id}/attendance`),
      ]);
      setDetail(detailData);
      setAllGuardians(guardiansData);
      setDetailEnrollments(enrollmentsData);
      setDetailFin(finData);
      setDetailAtt(attData);
      setLinkForm({ guardian_id: "", relationship: "responsavel", is_primary: false });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const refreshDetail = async () => {
    const { data } = await api.get(`/students/${detail.id}`);
    setDetail(data);
    load();
  };

  const linkGuardian = async () => {
    if (!linkForm.guardian_id) return;
    try {
      await api.post(`/students/${detail.id}/guardians`, linkForm);
      toast.success("Responsável vinculado.");
      setLinkForm({ guardian_id: "", relationship: "responsavel", is_primary: false });
      refreshDetail();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const unlinkGuardian = async (guardianId) => {
    try {
      await api.delete(`/students/${detail.id}/guardians/${guardianId}`);
      toast.success("Vínculo removido.");
      refreshDetail();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setPrimary = async (guardianId) => {
    try {
      await api.put(`/students/${detail.id}/guardians/${guardianId}`, { is_primary: true });
      toast.success("Responsável principal definido.");
      refreshDetail();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const linkedIds = detail ? detail.guardians.map((g) => g.id) : [];
  const linkableGuardians = allGuardians.filter((g) => !linkedIds.includes(g.id));

  return (
    <div className="space-y-6" data-testid="students-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold">Alunos</h1>
          <p className="mt-1 text-sm text-muted-foreground">Cadastro administrativo de alunos e seus responsáveis.</p>
        </div>
        <Button onClick={() => openDialog(null)} className="bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="student-add-button">
          <Plus size={16} className="mr-2" /> Novo aluno
        </Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <form
          onSubmit={(e) => { e.preventDefault(); load(); }}
          className="flex flex-1 items-center gap-2"
        >
          <Input
            placeholder="Pesquisar por nome ou CPF..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="student-search-input"
          />
          <Button type="submit" variant="outline" size="icon" data-testid="student-search-button">
            <Search size={16} />
          </Button>
        </form>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-44" data-testid="student-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os status</SelectItem>
            {Object.entries(STATUS_LABELS).map(([v, l]) => (
              <SelectItem key={v} value={v}>{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-wider">Nome</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Idade</TableHead>
              <TableHead className="hidden text-xs uppercase tracking-wider md:table-cell">CPF</TableHead>
              <TableHead className="hidden text-xs uppercase tracking-wider lg:table-cell">Responsáveis</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-wider">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {students.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="p-6 text-sm text-muted-foreground" data-testid="students-empty">
                  Nenhum aluno encontrado.
                </TableCell>
              </TableRow>
            )}
            {students.map((s) => (
              <TableRow key={s.id} className="transition-colors hover:bg-zinc-50" data-testid={`student-row-${s.id}`}>
                <TableCell className="font-medium">{s.full_name}</TableCell>
                <TableCell>{s.age} anos</TableCell>
                <TableCell className="hidden md:table-cell">{fmtCPF(s.cpf)}</TableCell>
                <TableCell className="hidden lg:table-cell">
                  <Badge variant="secondary">{s.guardians_count}</Badge>
                </TableCell>
                <TableCell><StatusBadge status={s.status} /></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" title="Visualizar" onClick={() => openDetail(s)} data-testid={`student-view-${s.id}`}>
                    <UserRound size={16} />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => openDialog(s)} data-testid={`student-edit-${s.id}`}>
                    <Pencil size={16} />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setDeleting(s)} data-testid={`student-delete-${s.id}`}>
                    <Trash2 size={16} className="text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="student-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar aluno" : "Novo aluno"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            {error && <p className="border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive" data-testid="student-form-error">{error}</p>}
            <div className="space-y-2">
              <Label>Nome completo *</Label>
              <Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="student-name-input" />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Data de nascimento *</Label>
                <Input type="date" required value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} data-testid="student-birthdate-input" />
                {form.birth_date && (
                  <p className="text-xs text-muted-foreground" data-testid="student-computed-age">
                    Idade: {calcAge(form.birth_date)} anos
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>CPF (opcional)</Label>
                <Input value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} placeholder="000.000.000-00" data-testid="student-cpf-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>WhatsApp (opcional)</Label>
                <Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="(11) 99999-9999" data-testid="student-whatsapp-input" />
              </div>
              <div className="space-y-2">
                <Label>E-mail (opcional)</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="student-email-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Endereço (opcional)</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="student-address-input" />
            </div>
            <div className="space-y-2">
              <Label>Foto de perfil — URL (opcional)</Label>
              <Input type="url" value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} placeholder="https://..." data-testid="student-photo-input" />
            </div>
            <div className="space-y-2">
              <Label>Observações administrativas</Label>
              <Textarea rows={2} value={form.admin_notes} onChange={(e) => setForm({ ...form, admin_notes: e.target.value })} data-testid="student-notes-input" />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="student-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABELS).map(([v, l]) => (
                    <SelectItem key={v} value={v}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={saving} className="w-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="student-save-button">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="student-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detail?.full_name}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Idade</p><p className="font-medium" data-testid="student-detail-age">{detail.age} anos</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Nascimento</p><p className="font-medium">{fmtBR(detail.birth_date)}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Status</p><StatusBadge status={detail.status} /></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">CPF</p><p className="font-medium">{fmtCPF(detail.cpf)}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">WhatsApp</p><p className="font-medium">{detail.whatsapp || "—"}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">E-mail</p><p className="font-medium">{detail.email || "—"}</p></div>
                <div className="col-span-2 sm:col-span-3"><p className="text-xs uppercase tracking-wider text-muted-foreground">Endereço</p><p className="font-medium">{detail.address || "—"}</p></div>
                {detail.admin_notes && (
                  <div className="col-span-2 sm:col-span-3"><p className="text-xs uppercase tracking-wider text-muted-foreground">Observações</p><p className="font-medium">{detail.admin_notes}</p></div>
                )}
              </div>

              <div>
                <h3 className="font-heading text-base font-semibold">Responsáveis vinculados</h3>
                <div className="mt-2 space-y-2" data-testid="student-guardians-list">
                  {detail.guardians.length === 0 && (
                    <p className="text-sm text-muted-foreground" data-testid="student-guardians-empty">Nenhum responsável vinculado.</p>
                  )}
                  {detail.guardians.map((g) => (
                    <div key={g.link_id} className="flex flex-wrap items-center gap-2 border border-border p-3" data-testid={`student-guardian-${g.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">
                          {g.full_name}
                          {g.is_primary && (
                            <Badge className="ml-2 bg-primary text-primary-foreground" data-testid={`student-guardian-primary-${g.id}`}>Principal</Badge>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">{RELATIONSHIP_LABELS[g.relationship]} · {g.whatsapp}</p>
                      </div>
                      {!g.is_primary && (
                        <Button variant="ghost" size="icon" title="Definir como principal" onClick={() => setPrimary(g.id)} data-testid={`student-guardian-set-primary-${g.id}`}>
                          <Star size={16} />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" title="Remover vínculo" onClick={() => unlinkGuardian(g.id)} data-testid={`student-guardian-unlink-${g.id}`}>
                        <Trash2 size={16} className="text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>

                <div className="mt-4 space-y-3 border border-border bg-zinc-50 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Vincular responsável</p>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <Select value={linkForm.guardian_id} onValueChange={(v) => setLinkForm({ ...linkForm, guardian_id: v })}>
                      <SelectTrigger data-testid="link-guardian-select"><SelectValue placeholder="Selecione o responsável" /></SelectTrigger>
                      <SelectContent>
                        {linkableGuardians.map((g) => (
                          <SelectItem key={g.id} value={g.id}>{g.full_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={linkForm.relationship} onValueChange={(v) => setLinkForm({ ...linkForm, relationship: v })}>
                      <SelectTrigger data-testid="link-relationship-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(RELATIONSHIP_LABELS).map(([v, l]) => (
                          <SelectItem key={v} value={v}>{l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={linkForm.is_primary}
                        onCheckedChange={(v) => setLinkForm({ ...linkForm, is_primary: !!v })}
                        data-testid="link-primary-checkbox"
                      />
                      Responsável principal
                    </label>
                    <Button
                      size="sm"
                      disabled={!linkForm.guardian_id}
                      onClick={linkGuardian}
                      className="bg-primary text-primary-foreground transition-colors hover:bg-primary/90"
                      data-testid="link-guardian-button"
                    >
                      Vincular
                    </Button>
                  </div>
                  {linkableGuardians.length === 0 && (
                    <p className="text-xs text-muted-foreground">Nenhum responsável disponível. Cadastre na página Responsáveis.</p>
                  )}
                </div>
              </div>

              <div>
                <h3 className="font-heading text-base font-semibold">Matrículas</h3>
                <div className="mt-2 space-y-2" data-testid="student-enrollments-list">
                  {detailEnrollments.length === 0 && (
                    <p className="text-sm text-muted-foreground" data-testid="student-enrollments-empty">
                      Nenhuma matrícula registrada.
                    </p>
                  )}
                  {detailEnrollments.map((en) => (
                    <div key={en.id} className="flex flex-wrap items-center gap-2 border border-border p-3" data-testid={`student-enrollment-${en.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">{en.course_name} · {en.teacher_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {en.modality_name} · {WEEKDAY_NAMES[en.weekday]} {en.start_time}–{en.end_time} · {en.room_name} · início {fmtBR(en.start_date)}
                        </p>
                      </div>
                      <EnrollmentStatusBadge status={en.status} />
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Novas matrículas são criadas na página Matrículas.
                </p>
              </div>

              <div>
                <h3 className="font-heading text-base font-semibold">Financeiro</h3>
                {!detailFin ? (
                  <p className="mt-2 text-sm text-muted-foreground" data-testid="student-financial-empty">
                    Nenhuma cobrança registrada para este aluno.
                  </p>
                ) : (
                  <div className="mt-2 space-y-3" data-testid="student-financial-section">
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                      <div className="border border-border p-2" data-testid="student-fin-total-charged">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Cobrado</p>
                        <p className="font-heading text-base font-bold">{fmtBRL(detailFin.totals.total_charged)}</p>
                      </div>
                      <div className="border border-border p-2" data-testid="student-fin-total-paid">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Pago</p>
                        <p className="font-heading text-base font-bold">{fmtBRL(detailFin.totals.total_paid)}</p>
                      </div>
                      <div className="border border-border p-2" data-testid="student-fin-total-open">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Em aberto</p>
                        <p className="font-heading text-base font-bold">{fmtBRL(detailFin.totals.total_open)}</p>
                      </div>
                      <div className="border border-border p-2" data-testid="student-fin-overdue-count">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Vencidas</p>
                        <p className="font-heading text-base font-bold">{detailFin.totals.overdue_count}</p>
                      </div>
                    </div>
                    <div className="space-y-2" data-testid="student-financial-charges">
                      {detailFin.charges.map((c) => (
                        <div key={c.id} className="flex flex-wrap items-center gap-2 border border-border p-3" data-testid={`student-financial-charge-${c.id}`}>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium">
                              {fmtMonthBR(c.reference_month)} · {fmtBRL(c.amount)}
                              {c.paid_amount > 0 && (
                                <span className="text-muted-foreground"> · pago {fmtBRL(c.paid_amount)}</span>
                              )}
                            </p>
                            <p className="text-xs text-muted-foreground">Vencimento {fmtBR(c.due_date)}</p>
                          </div>
                          <ChargeStatusBadge status={c.status} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <h3 className="font-heading text-base font-semibold">Frequência</h3>
                {detailAtt && (
                  <div className="mt-2 space-y-3" data-testid="student-attendance-section">
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                      <div className="border border-border p-2" data-testid="student-att-registered">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Registradas</p>
                        <p className="font-heading text-base font-bold">{detailAtt.totals.registradas}</p>
                      </div>
                      <div className="border border-border p-2" data-testid="student-att-present">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Presenças</p>
                        <p className="font-heading text-base font-bold">{detailAtt.totals.presencas}</p>
                      </div>
                      <div className="border border-border p-2" data-testid="student-att-absent">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Faltas</p>
                        <p className="font-heading text-base font-bold">{detailAtt.totals.faltas}</p>
                      </div>
                      <div className="border border-border p-2" data-testid="student-att-percent">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Frequência</p>
                        <p className="font-heading text-base font-bold">
                          {detailAtt.totals.percentual != null ? `${detailAtt.totals.percentual}%` : "—"}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2" data-testid="student-attendance-history">
                      {detailAtt.lessons.filter((l) => l.attendance).length === 0 && (
                        <p className="text-sm text-muted-foreground" data-testid="student-attendance-empty">
                          Nenhuma aula com frequência registrada.
                        </p>
                      )}
                      {detailAtt.lessons.filter((l) => l.attendance).map((l) => (
                        <div key={l.id} className="border border-border p-3" data-testid={`student-attendance-lesson-${l.id}`}>
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-sm font-medium">
                              {fmtBR(l.date)} · {l.start_time}–{l.end_time} · {l.teacher_name}
                            </p>
                            <Badge
                              variant={l.attendance === "falta" ? "destructive" : "default"}
                              className={l.attendance === "presente" ? "bg-primary text-primary-foreground" : ""}
                            >
                              {l.attendance === "presente" ? "Presente" : "Falta"}
                            </Badge>
                          </div>
                          {l.diary_content && <p className="mt-1 text-xs text-muted-foreground">Conteúdo: {l.diary_content}</p>}
                          {l.diary_notes && <p className="mt-0.5 text-xs text-muted-foreground">Observação: {l.diary_notes}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-heading text-base font-semibold">Acompanhamento pedagógico</h3>
                  <Button size="sm" variant="outline" onClick={() => setReportOpen(true)} data-testid="student-report-button">
                    Relatório pedagógico
                  </Button>
                </div>
                <div className="mt-2 space-y-4" data-testid="student-pedagogy-section">
                  {detailEnrollments.filter((e) => e.status === "ativa").length === 0 && (
                    <p className="text-sm text-muted-foreground" data-testid="student-pedagogy-empty">
                      Nenhuma matrícula ativa para acompanhamento.
                    </p>
                  )}
                  {detailEnrollments
                    .filter((e) => e.status === "ativa")
                    .map((en) => (
                      <div key={en.id} className="border border-border p-3" data-testid={`student-pedagogy-enrollment-${en.id}`}>
                        <p className="text-sm font-medium">
                          {en.course_name} · {en.teacher_name}
                        </p>
                        <div className="mt-2">
                          <PedagogyPanel enrollmentId={en.id} testIdPrefix={`student-pedagogy-${en.id}`} />
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {reportOpen && detail && (
        <PedagogicalReportDialog studentId={detail.id} studentName={detail.full_name} onClose={() => setReportOpen(false)} />
      )}

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={() => setDeleting(null)}
        title="Excluir aluno"
        description={`Deseja excluir "${deleting?.full_name}"? Os vínculos com responsáveis também serão removidos.`}
        onConfirm={remove}
        testId="student-delete-confirm"
      />
    </div>
  );
}
